/**
 * DashboardDetailPage (P3-S2 Doc 42).
 */
import { ArrowLeft, FileText, Loader2, RefreshCw, Trash2 } from "lucide-react";
import * as React from "react";

import { type DashboardOut, dashboardsApi } from "@/api/dashboards";
import { DashboardRenderer } from "@/components/dashboard/DashboardRenderer";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { useRouteStore } from "@/stores/route";
import { useUIStore } from "@/stores/ui";

interface Props {
  code: string;
}

export function DashboardDetailPage({ code }: Props): JSX.Element {
  const navigate = useRouteStore((s) => s.navigate);
  const pushToast = useUIStore((s) => s.pushToast);
  const [dashboard, setDashboard] = React.useState<DashboardOut | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [showJson, setShowJson] = React.useState(false);
  const [refreshKey, setRefreshKey] = React.useState(0);

  const refresh = React.useCallback(async () => {
    setLoading(true);
    try {
      const r = await dashboardsApi.get(code);
      setDashboard(r);
    } catch (e) {
      pushToast({
        variant: "destructive",
        title: "Không tải được dashboard",
        description: e instanceof Error ? e.message : String(e),
      });
    } finally {
      setLoading(false);
    }
  }, [code, pushToast]);

  React.useEffect(() => {
    void refresh();
  }, [refresh]);

  const handleArchive = async (): Promise<void> => {
    if (!dashboard) return;
    if (!confirm(`Archive dashboard "${dashboard.title}"?`)) return;
    try {
      await dashboardsApi.archive(code);
      pushToast({ variant: "success", title: "Đã archive" });
      navigate("admin.dashboards");
    } catch (e) {
      pushToast({
        variant: "destructive",
        title: "Archive thất bại",
        description: e instanceof Error ? e.message : String(e),
      });
    }
  };

  if (loading) {
    return (
      <div className="p-8 flex items-center justify-center text-muted-foreground gap-2">
        <Loader2 className="size-4 animate-spin" />
        Đang tải...
      </div>
    );
  }

  if (!dashboard) {
    return (
      <div className="p-8 text-center text-muted-foreground">
        Dashboard <code>{code}</code> không tồn tại.{" "}
        <Button
          variant="link"
          onClick={() => navigate("admin.dashboards")}
          className="px-1"
        >
          ← Về list
        </Button>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto p-4 space-y-4">
      <header className="flex items-center gap-2 flex-wrap">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate("admin.dashboards")}
        >
          <ArrowLeft className="size-4 mr-1" /> Dashboards
        </Button>
        <div className="flex-1 min-w-0">
          <h1 className="text-lg font-semibold truncate">{dashboard.title}</h1>
          <code className="text-[11px] text-muted-foreground">
            {dashboard.code}
          </code>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => setRefreshKey((k) => k + 1)}
          title="Refresh widget data"
        >
          <RefreshCw className="size-4 mr-1" /> Refresh
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={() => setShowJson((v) => !v)}
          title="View spec JSON"
        >
          <FileText className="size-4 mr-1" /> {showJson ? "Hide" : "View"} JSON
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => void handleArchive()}
          className="text-muted-foreground hover:text-destructive"
        >
          <Trash2 className="size-4" />
        </Button>
      </header>

      {dashboard.description ? (
        <p className="text-sm text-muted-foreground">{dashboard.description}</p>
      ) : null}

      {showJson ? (
        <Card>
          <CardContent className="p-3">
            <pre className="text-[10px] bg-muted p-2 rounded overflow-x-auto whitespace-pre-wrap max-h-96">
              {JSON.stringify(dashboard.spec, null, 2)}
            </pre>
          </CardContent>
        </Card>
      ) : null}

      <DashboardRenderer spec={dashboard.spec} refreshKey={refreshKey} />
    </div>
  );
}
